/**
 * PlaceWildlife applier.
 *
 * Sets the wildlife on the target board cell.
 * Clears draft.tokenInstanceId.
 * Awards +1 nature token if the tile is a keystone with no landmark (§6.4).
 * Detects 5+ corridor trigger for Landmarks module (§9.1).
 * Advances phase: placeWildlife → unlockLandmark (if trigger) or endTurn.
 *
 * Corridor detection (§9.1 + §5):
 *   After placement, for each habitat H: check if any corridor containing the
 *   just-placed tile coord has size ≥ 5 using the touching-edge pair definition.
 *   Two tiles are habitat-connected on H iff the edge of tile A facing tile B
 *   carries H AND the reciprocal edge of tile B facing tile A also carries H.
 *
 * Spec §6.4, §9.1, §5.
 */

import { LANDMARK_CARDS_BY_ID } from "../data/landmark-cards";
import { TILE_DEFS } from "../data/tile-defs";
import type { PlaceWildlifeAction } from "../types/actions";
import type { AxialCoord } from "../types/coords";
import { coordKey, NEIGHBOUR_OFFSETS } from "../types/coords";
import type { GameState } from "../types/game-state";
import type { Habitat } from "../types/habitat";
import { HABITATS } from "../types/habitat";
import type { Board } from "../types/player";
import { bumpVersion } from "./common-applier";

// ---------------------------------------------------------------------------
// Tile def lookup
// ---------------------------------------------------------------------------

const findTileDef = (tileDefId: string) =>
  TILE_DEFS.find((d) => d.tileDefId === tileDefId);

// ---------------------------------------------------------------------------
// Corridor detection (§5)
// ---------------------------------------------------------------------------

/**
 * Returns true iff tileA (at the edge `edgeIdxA` facing B) and tileB
 * (at the reciprocal edge facing A) are both H on their touching edge pair.
 */
const areHabitatConnectedOnEdge = (
  tileDefIdA: string,
  edgeIdxA: number,
  tileDefIdB: string,
  habitat: Habitat,
): boolean => {
  const defA = findTileDef(tileDefIdA);
  const defB = findTileDef(tileDefIdB);
  if (defA === undefined || defB === undefined) return false;

  const offset = NEIGHBOUR_OFFSETS[edgeIdxA];
  if (offset === undefined) return false;

  return (
    defA.edges[edgeIdxA] === habitat &&
    defB.edges[offset.reciprocal] === habitat
  );
};

/**
 * BFS to find the connected corridor of habitat H that contains `start`.
 * Connectivity = touching-edge pair both carry H (spec §5).
 */
const getHabitatCorridor = (
  board: Board,
  start: AxialCoord,
  habitat: Habitat,
): readonly AxialCoord[] => {
  const startCell = board.cells[coordKey(start)];
  if (startCell === undefined) return [];

  const startDef = findTileDef(startCell.tileDefId);
  if (startDef === undefined || !startDef.edges.includes(habitat)) return [];

  const visited = new Set<string>();
  const result: AxialCoord[] = [];
  const queue: AxialCoord[] = [start];
  visited.add(coordKey(start));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentKey = coordKey(current);
    const currentCell = board.cells[currentKey];
    if (currentCell === undefined) continue;

    result.push(current);

    for (const offset of NEIGHBOUR_OFFSETS) {
      const neighbour: AxialCoord = {
        q: current.q + offset.dq,
        r: current.r + offset.dr,
      };
      const nKey = coordKey(neighbour);
      if (visited.has(nKey)) continue;
      visited.add(nKey);

      const neighbourCell = board.cells[nKey];
      if (neighbourCell === undefined) continue;

      if (
        areHabitatConnectedOnEdge(
          currentCell.tileDefId,
          offset.edge,
          neighbourCell.tileDefId,
          habitat,
        )
      ) {
        queue.push(neighbour);
      }
    }
  }

  return result;
};

/**
 * Returns habitats for which placing the tile at `placedCoord` causes a
 * corridor of size ≥ 5, and the player has not already placed a landmark
 * for that habitat (§9.1).
 */
const detectLandmarkTriggerHabitats = (
  board: Board,
  placedCoord: AxialCoord,
  existingLandmarkCardIds: readonly string[],
): readonly Habitat[] => {
  const alreadyHasHabitat = new Set<Habitat>();
  for (const cardId of existingLandmarkCardIds) {
    const card = LANDMARK_CARDS_BY_ID[cardId];
    if (card !== undefined) alreadyHasHabitat.add(card.habitat);
  }

  const triggered: Habitat[] = [];
  for (const habitat of HABITATS) {
    if (alreadyHasHabitat.has(habitat)) continue;
    const corridor = getHabitatCorridor(board, placedCoord, habitat);
    if (corridor.length >= 5) {
      triggered.push(habitat);
    }
  }
  return triggered;
};

// ---------------------------------------------------------------------------
// Applier
// ---------------------------------------------------------------------------

export const applyPlaceWildlife = (
  state: GameState,
  action: PlaceWildlifeAction,
): GameState => {
  const activeSeat = state.turn.activeSeat;
  const activePlayer = state.players[activeSeat]!;
  const key = coordKey(action.coord);
  const cell = activePlayer.board.cells[key]!;

  // Resolve wildlife species from the tile def's first wildlifeOption.
  // The current state model does not track which species the drafted token is.
  // The validator ensures the cell's tile is compatible; the species comes
  // from the tile def. See validator comment in place-wildlife.validator.ts.
  const tileDef = findTileDef(cell.tileDefId);
  const wildlifeSpecies = tileDef?.wildlifeOptions[0] ?? null;

  // Keystone bonus: +1 nature token if keystone and no landmark (§6.4)
  const isKeystone = tileDef?.keystone === true;
  const hasLandmark = cell.landmark !== null;
  const keystoneBonus = isKeystone && !hasLandmark ? 1 : 0;

  // Build updated board with wildlife placed
  const updatedBoard: Board = {
    cells: {
      ...activePlayer.board.cells,
      [key]: {
        ...cell,
        wildlife: wildlifeSpecies,
      },
    },
  };

  // Landmarks trigger: check corridor size using the just-placed tile coord
  // (spec §9.1 — triggered by PlaceTile, detected at PlaceWildlife resolution)
  const placedCoord =
    state.turn.actionBudget.justPlacedTileCoord ?? action.coord;

  let landmarkEligibleHabitats: readonly Habitat[] = [];
  if (state.ruleset.modules.landmarks) {
    landmarkEligibleHabitats = detectLandmarkTriggerHabitats(
      updatedBoard,
      placedCoord,
      activePlayer.landmarkPicks,
    );
  }

  const nextPhase =
    landmarkEligibleHabitats.length > 0 ? "unlockLandmark" : "endTurn";

  return {
    ...state,
    ...bumpVersion(state),
    turn: {
      ...state.turn,
      phase: nextPhase,
      actionBudget: {
        ...state.turn.actionBudget,
        landmarkEligibleHabitats,
      },
    },
    players: state.players.map((p) =>
      p.seat === activeSeat
        ? {
            ...p,
            natureTokens: p.natureTokens + keystoneBonus,
            draft: {
              ...p.draft,
              tokenInstanceId: null,
            },
            board: updatedBoard,
          }
        : p,
    ),
  };
};
